import { useState, useEffect, useRef, useCallback, createContext } from "react";
import { ref, set, child } from "firebase/database";
import { useDataTrack } from "hooks/livekit/useDataTrack/useDataTrack";
import { database } from "hooks/useFirebase";
import useVideoContext from "hooks/livekit/useVideoContext/useVideoContext";

export const useFirebaseRef = () => {
  return ref(database, "okoServer");
};

export const useGestureState = () => {
  const firebaseRef = useFirebaseRef();
  const [participantGestures] = useState({});
  const [singleHandGesture, setSingleHandGesture] = useState(null);
  const isMountedRef = useRef(true);

  // Track mounted state to prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { room } = useVideoContext();
  const localParticipant = room?.localParticipant;

  // Memoize the data track handler to prevent unnecessary re-subscriptions
  const handleDataTrack = useCallback((e) => {
    if (!isMountedRef.current) return;
    if (!localParticipant?.userId) return;

    const participantId = e?.data?.oko_user_id;

    if (localParticipant.userId !== participantId) return;
    if (
      e?.data?.gesture_type !== "GazeDirection" &&
      e?.data?.gesture_type !== "SingleHandAndFace"
    ) {
      setSingleHandGesture(prev => {
        if (prev?.detection?.uuid === e?.data?.detection?.uuid) return prev;
        return e.data;
      });
    }
  }, [localParticipant?.userId]);

  useDataTrack(handleDataTrack);

  useEffect(() => {
    if (!localParticipant?.sid || !isMountedRef.current) return;

    const participantRef = child(firebaseRef, localParticipant.sid);
    set(participantRef, singleHandGesture)
      .catch(error => {
        // Only log if still mounted - permission denied is expected when session ends
        if (isMountedRef.current) {
          console.error("Error setting gesture data:", error);
        }
      });
  }, [singleHandGesture, firebaseRef, localParticipant]);

  return { participantGestures };
};

const Context = createContext();

export const GestureStateProvider = ({ children }) => {
  const value = useGestureState();
  return <Context.Provider value={value}>{children}</Context.Provider>;
};
